import { useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'

import { useDigitalProductContext } from '@/shared/contexts/DigitalProductContext'
import type { ObjectiveWithProgress } from '@/types/objective'

import { fetchObjectives } from '../api'
import { buildGoals, goalProgressUnits } from '../model/milestones'

export const useObjectives = () => {
  const { digitalProductId } = useDigitalProductContext()

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['objectives', digitalProductId],
    queryFn: () => fetchObjectives(digitalProductId!),
    enabled: !!digitalProductId,
    refetchInterval: 60_000,
    staleTime: 30_000
  })

  const objectives = data ?? []

  // Meta = escada de marcos ou objetivo solto (é o que a lista desenha). Já os
  // contadores do hero usam a unidade de atuação do participante: cada etapa de
  // uma escada e cada objetivo solto. Contar só metas escondia o avanço de quem
  // cumpria etapas de uma escada grande.
  const goals = useMemo(() => buildGoals(objectives), [objectives])

  const { completedCount, totalCount } = useMemo(
    () =>
      goals.reduce(
        (acc, goal) => {
          const { done, total } = goalProgressUnits(goal)
          return { completedCount: acc.completedCount + done, totalCount: acc.totalCount + total }
        },
        { completedCount: 0, totalCount: 0 }
      ),
    [goals]
  )

  const overallPercent = useMemo(
    () => (totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0),
    [completedCount, totalCount]
  )

  // Continua somando todos os objetivos, agrupados ou não: é dinheiro real, e
  // agrupar não muda quanto o participante pode ganhar.
  const totalPossibleCoins = useMemo(
    () => objectives.reduce((acc, o) => acc + (o.coin_reward ?? 0), 0),
    [objectives]
  )

  const earnedCoins = useMemo(
    () =>
      objectives.reduce(
        (acc, objective) => acc + (objective.completed ? (objective.coin_reward ?? 0) : 0),
        0
      ),
    [objectives]
  )

  return {
    objectives,
    goals,
    isLoading,
    isError,
    refetch,
    completedCount,
    totalCount,
    overallPercent,
    totalPossibleCoins,
    earnedCoins
  }
}
